import React, { useMemo } from "react";

import { MemoizedMarkdown } from "../../../../../components/Markdown";
import { InlineChartBubble } from "./InlineChartBubble";
import { InlineTableBubble } from "./InlineTableBubble";
import { SuggestionChips } from "./SuggestionChips";
import { TextAnswerBubble } from "./TextAnswerBubble";

import type { ChatMessage, ChatOutput } from "../types";

import { useReportingChatStore } from "../store";

const OutputRenderer: React.FC<{ output: ChatOutput; messageId: string; defaultName: string }> = ({
	output,
	messageId,
	defaultName,
}) => {
	if (output.kind === "answer")
		return <TextAnswerBubble output={output} messageId={messageId} defaultName={defaultName} />;
	if (output.kind === "table") return <InlineTableBubble output={output} defaultName={defaultName} />;
	return <InlineChartBubble output={output} defaultName={defaultName} />;
};

export const ChatMessageItem: React.FC<{
	message: ChatMessage;
	onSuggestionClick: (text: string) => void;
	suggestionsDisabled?: boolean;
}> = ({ message, onSuggestionClick, suggestionsDisabled }) => {
	const isUser = message.role === "user";
	const messages = useReportingChatStore((state) => state.messages);

	// The nearest preceding user turn — used as the save-modal default name when an output has no
	// title of its own (answers).
	const originatingQuery = useMemo(() => {
		const index = messages.findIndex((candidate) => candidate.id === message.id);
		if (index === -1) return undefined;
		for (let i = index - 1; i >= 0; i -= 1) {
			if (messages[i].role === "user") return messages[i].content;
		}
		return undefined;
	}, [messages, message.id]);

	if (isUser) {
		return (
			<div className="flex w-full justify-center px-4 py-2">
				<div className="flex w-full max-w-3xl justify-end">
					<div className="max-w-[80%] rounded-lg bg-primary-5 px-3 py-2 text-sm whitespace-pre-wrap text-white">
						{message.content}
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="flex w-full justify-center px-4 py-2">
			<div className="w-full max-w-3xl text-sm text-neutral-800">
				{message.content && (
					<MemoizedMarkdown content={message.content} id={message.id} disallowImages restrictExternalLinks />
				)}
				{message.isStreaming && !message.content && <span className="text-neutral-400">Thinking…</span>}
				{message.outputs?.map((output, index) => {
					const defaultName = ("title" in output && output.title) || originatingQuery || "Untitled report";
					return (
						<div key={index}>
							<OutputRenderer output={output} messageId={message.id} defaultName={defaultName} />
							<SuggestionChips
								suggestions={output.suggestions}
								onSelect={onSuggestionClick}
								disabled={suggestionsDisabled}
							/>
						</div>
					);
				})}
				{message.isIncomplete && (
					<p className="mt-1 text-xs text-neutral-400">This response may be incomplete.</p>
				)}
			</div>
		</div>
	);
};

export default ChatMessageItem;
